import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Check, Pause, Play } from 'lucide-react';
import type {
  PodcastNarrator,
  PodcastNarratorSource,
  PodcastSettings,
  PodcastVoiceOption,
} from '@almanac/contracts';
import { useApi } from '../api/useApi';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; settings: PodcastSettings };

/** The outcome of the last save, shown under the control that made it. */
type Notice =
  | { kind: 'saved'; voiceName: string; from: 'list' | 'custom' }
  | { kind: 'error'; message: string; from: 'list' | 'custom' };

const SOURCE_NOTE: Record<PodcastNarratorSource, string> = {
  setting: ' (chosen in Almanac)',
  guidance_note: ' (from your Weekly brief preferences note)',
  default: ' (the default)',
};

/** API errors arrive as the response body; show its `error` field when it has one. */
function errorMessage(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      'error' in parsed &&
      typeof parsed.error === 'string'
    )
      return parsed.error;
  } catch {
    // Not JSON: show it as is.
  }
  return raw;
}

/**
 * The Podcast tab's Narrator card: the voice that reads the caller's weekly
 * brief, the curated voices with free previews, and an advanced field for the
 * id of any other voice in Langston's ElevenLabs account. `onPreviewStart`
 * receives each preview player as it starts, so the page can pause its other
 * audio; pausing that player stops the preview.
 */
export function NarratorCard({
  onPreviewStart,
}: {
  onPreviewStart?: (player: HTMLAudioElement) => void;
} = {}) {
  const api = useApi();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [customId, setCustomId] = useState('');
  const preview = usePreview(onPreviewStart);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      let next: LoadState;
      try {
        next = { status: 'ready', settings: await api.getPodcastSettings() };
      } catch (e: unknown) {
        next = { status: 'error', message: errorMessage(e) };
      }
      if (!cancelled) setState(next);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [api, attempt]);

  async function save(voiceId: string, from: Notice['from']) {
    setSaving(voiceId);
    setNotice(null);
    try {
      const result = await api.setPodcastVoice(voiceId);
      if (result.status === 'saved') {
        setState({ status: 'ready', settings: result.settings });
        setNotice({ kind: 'saved', voiceName: result.settings.narrator.voice_name, from });
        if (from === 'custom') setCustomId('');
      } else {
        setNotice({ kind: 'error', message: result.message, from });
      }
    } catch (e: unknown) {
      setNotice({ kind: 'error', message: errorMessage(e), from });
    } finally {
      setSaving(null);
    }
  }

  function onSubmitCustom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const id = customId.trim();
    if (id) void save(id, 'custom');
  }

  return (
    <section
      aria-labelledby="podcast-narrator"
      className="mt-6 rounded-xl border border-border bg-card p-4 sm:p-5"
    >
      <h2 id="podcast-narrator" className="font-display text-lg font-semibold text-foreground">
        Narrator
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The voice that reads your weekly brief. A change applies from the next episode.
      </p>

      {state.status === 'loading' ? (
        <div className="mt-4 h-40 animate-pulse rounded-lg bg-muted" aria-hidden />
      ) : null}
      {state.status === 'error' ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <p className="text-danger">{state.message}</p>
          <button
            type="button"
            onClick={() => {
              setState({ status: 'loading' });
              setAttempt((n) => n + 1);
            }}
            className="inline-flex shrink-0 items-center rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Try again
          </button>
        </div>
      ) : null}
      {state.status === 'ready' ? (
        <>
          <CurrentNarrator narrator={state.settings.narrator} />
          {state.settings.voices_unavailable ? (
            <p className="mt-3 text-sm text-muted-foreground">
              The voice list is unavailable right now. Your brief still uses the voice above.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
              {state.settings.voices.map((voice) => (
                <li key={voice.voice_id}>
                  <VoiceRow
                    voice={voice}
                    selected={voice.voice_id === state.settings.narrator.voice_id}
                    saving={saving === voice.voice_id}
                    disabled={saving !== null}
                    playing={preview.playingId === voice.voice_id}
                    onPreview={() => preview.toggle(voice)}
                    onSelect={() => void save(voice.voice_id, 'list')}
                  />
                </li>
              ))}
            </ul>
          )}
          {notice?.from === 'list' ? <NoticeText notice={notice} /> : null}

          <details className="mt-4 text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Use another voice ID
            </summary>
            <p className="mt-2 text-muted-foreground">
              Any voice already in Langston&apos;s ElevenLabs account works. Paste its voice ID from
              ElevenLabs.
            </p>
            <form onSubmit={onSubmitCustom} className="mt-2 flex flex-wrap items-center gap-2">
              <label htmlFor="narrator-voice-id" className="sr-only">
                ElevenLabs voice ID
              </label>
              <input
                id="narrator-voice-id"
                value={customId}
                onChange={(event) => setCustomId(event.target.value)}
                placeholder="e.g. TWutjvRaJqAX89preB4e"
                autoComplete="off"
                spellCheck={false}
                className="min-w-0 flex-1 rounded-md border border-border bg-background px-2.5 py-1.5 font-mono text-xs text-foreground"
              />
              <button
                type="submit"
                disabled={saving !== null || customId.trim() === ''}
                className="inline-flex shrink-0 items-center rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                {saving !== null && saving === customId.trim() ? 'Checking…' : 'Check and use'}
              </button>
            </form>
            {notice?.from === 'custom' ? <NoticeText notice={notice} /> : null}
          </details>
        </>
      ) : null}
    </section>
  );
}

function CurrentNarrator({ narrator }: { narrator: PodcastNarrator }) {
  return (
    <p className="mt-4 text-sm text-foreground">
      Your brief is narrated by <span className="font-medium">{narrator.voice_name}</span>
      <span className="text-muted-foreground">{SOURCE_NOTE[narrator.source]}</span>.
    </p>
  );
}

function NoticeText({ notice }: { notice: Notice }) {
  if (notice.kind === 'saved') {
    return (
      <output className="mt-2 block text-xs text-muted-foreground">
        Saved. Your next brief is narrated by {notice.voiceName}.
      </output>
    );
  }
  return (
    <p role="alert" className="mt-2 text-xs text-danger">
      {notice.message}
    </p>
  );
}

interface VoiceRowProps {
  voice: PodcastVoiceOption;
  selected: boolean;
  saving: boolean;
  disabled: boolean;
  playing: boolean;
  onPreview: () => void;
  onSelect: () => void;
}

function VoiceRow({
  voice,
  selected,
  saving,
  disabled,
  playing,
  onPreview,
  onSelect,
}: VoiceRowProps) {
  let action = 'Use';
  if (saving) action = 'Saving…';
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
      <button
        type="button"
        onClick={onPreview}
        disabled={!voice.preview_url}
        aria-label={playing ? `Stop ${voice.alias} preview` : `Play ${voice.alias} preview`}
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
      >
        {playing ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{voice.alias}</span>
          {voice.gender ? (
            <span className="text-xs text-muted-foreground capitalize">{voice.gender}</span>
          ) : null}
          {voice.company_feed ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              Also voices the company feed
            </span>
          ) : null}
        </div>
        {voice.tagline ? <p className="text-xs text-muted-foreground">{voice.tagline}</p> : null}
      </div>
      {selected ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary">
          <Check size={14} />
          Selected
        </span>
      ) : (
        <button
          type="button"
          onClick={onSelect}
          disabled={disabled}
          aria-label={`Use ${voice.alias}`}
          className="inline-flex shrink-0 items-center rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
        >
          {action}
        </button>
      )}
    </div>
  );
}

/**
 * One preview plays at a time; it stops when the card unmounts. A player only
 * updates the button state while it is still the current one, so a late event
 * from a replaced preview cannot clear the new preview's controls.
 */
function usePreview(onStart?: (player: HTMLAudioElement) => void) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  useEffect(
    () => () => {
      audio.current?.pause();
    },
    [],
  );

  const toggle = useCallback(
    (voice: PodcastVoiceOption) => {
      audio.current?.pause();
      if (playingId === voice.voice_id || !voice.preview_url) {
        setPlayingId(null);
        return;
      }
      const player = new Audio(voice.preview_url);
      const stopped = () => {
        if (audio.current === player) setPlayingId(null);
      };
      // `pause` also covers the page pausing this preview for an episode.
      player.addEventListener('ended', stopped);
      player.addEventListener('pause', stopped);
      audio.current = player;
      setPlayingId(voice.voice_id);
      onStart?.(player);
      void player.play().catch(stopped);
    },
    [playingId, onStart],
  );

  return { playingId, toggle };
}
